import { CalendarClock, CheckCircle2 } from "lucide-react";
import CTASection from "../components/sections/common/CTASection";
import FAQAccordion from "../components/sections/common/FAQAccordion";
import PageHeroSplit from "../components/sections/common/PageHeroSplit";
import ProcessSection from "../components/sections/common/ProcessSection";
import PageMeta from "../components/ui/PageMeta";
import LogoPlaceholder from "../components/ui/LogoPlaceholder";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import { ctaPhone, pageCtaContent } from "../data/ctaContent";
import { faqPreviewForAdmissions } from "../data/faqs";
import { admissionsPagesContent } from "../data/innerPages";
import { servicesProcess } from "../data/services";
import {
  getCollegeDisplayName,
  getCollegeLogoByName,
  getCollegeStateByName
} from "../utils/collegeHelpers";

const content = admissionsPagesContent.mastersIndia;

const whenGuidanceHelps = [
  "When specialization options feel unclear",
  "When profile-to-college fit needs objective evaluation",
  "When application timelines need structured planning"
];

const MASTERS_HERO_IMAGE =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1400&q=80";

function MastersIndia() {
  return (
    <>
      <PageMeta title={content.meta.title} description={content.meta.description} />

      <PageHeroSplit
        badge={content.hero.badge}
        title={content.hero.title}
        description={content.hero.description}
        highlights={content.hero.highlights}
        primaryCta={{ label: "Plan Master's Journey", to: "/contact" }}
        secondaryCta={{ label: "Explore Pathways", to: "/colleges" }}
        sideTitle="Who this supports"
        sideDescription="For graduates making focused postgraduate decisions with clarity."
        sideItems={content.forWho}
        heroImage={MASTERS_HERO_IMAGE}
      />

      <Section className="bg-white">
        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-card sm:p-7">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-gold-strong">Overview</p>
          <p className="mt-3 text-base leading-relaxed text-brand-charcoal/84">{content.intro}</p>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {whenGuidanceHelps.map((item) => (
            <article key={item} className="rounded-2xl border border-brand-line bg-brand-sky/35 p-5 shadow-card">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-navy">
                <CalendarClock className="h-4 w-4" />
              </div>
              <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/84">{item}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
          <article className="rounded-2xl border border-brand-line bg-brand-sky/30 p-6 shadow-card">
            <p className="text-sm font-semibold text-brand-navy">Common decision areas</p>
            <ul className="mt-4 space-y-3">
              {content.decisionPoints.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-brand-charcoal/84">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-gold-strong" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </article>

          <div className="space-y-4">
            {content.supportBlocks.map((block) => (
              <article key={block.title} className="rounded-2xl border border-brand-line bg-white p-5 shadow-card">
                <h3 className="text-lg font-semibold text-brand-navy">{block.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/82">{block.description}</p>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading
          badge="College Direction Preview"
          title="Institutions often explored in master's counselling discussions"
          description="A curated glimpse of commonly discussed colleges for postgraduate planning context."
        />

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {content.collegePreview.map((college) => {
            const collegeName = getCollegeDisplayName(college);
            const logoUrl = getCollegeLogoByName(college);
            const collegeState = getCollegeStateByName(college);

            return (
              <article key={college} className="rounded-xl border border-brand-line bg-white px-4 py-3 shadow-card">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${collegeName} logo`}
                      className="h-10 w-10 rounded-lg border border-brand-line bg-white object-contain"
                    />
                  ) : (
                    <LogoPlaceholder
                      name={collegeName}
                      className="h-10 w-10 rounded-lg text-[11px]"
                    />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-brand-navy">{collegeName}</p>
                    <p className="text-xs text-brand-charcoal/72">{collegeState}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Section>

      <ProcessSection
        badge="Guidance Structure"
        title="How TheCareerSpace supports master's admissions"
        description="A reliable process for graduates planning the next academic stage."
        steps={servicesProcess}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeading
            badge="FAQ Preview"
            title="Common postgraduate counselling questions"
            description="Quick clarifications for families planning master's admissions in India."
          />
          <FAQAccordion items={faqPreviewForAdmissions} />
        </div>
      </Section>

      <CTASection
        {...pageCtaContent.mastersIndia}
        phone={ctaPhone}
        secondaryCtaClassName="border-brand-gold/55 bg-brand-gold-soft text-brand-navy hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
      />
    </>
  );
}

export default MastersIndia;
